import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CustomerListParams, CustomerStatusFilter } from '@/types/customer';

export interface CustomerQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string;
  email: string;
  city: string;
  state: string;
  pincode: string;
  status: CustomerStatusFilter;
  joinedFrom: string;
  joinedTo: string;
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search: string | null;
  email: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;
  status: CustomerStatusFilter | null;
  joinedFrom: string | null;
  joinedTo: string | null;
}>;

const DEFAULTS: CustomerQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  search: '',
  email: '',
  city: '',
  state: '',
  pincode: '',
  status: 'all',
  joinedFrom: '',
  joinedTo: '',
};

function parseStatus(value: string | null): CustomerStatusFilter {
  if (value === 'active' || value === 'inactive') return value;
  return 'all';
}

export function useCustomerQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<CustomerQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc',
      search: searchParams.get('search') || '',
      email: searchParams.get('email') || '',
      city: searchParams.get('city') || '',
      state: searchParams.get('state') || '',
      pincode: searchParams.get('pincode') || '',
      status: parseStatus(searchParams.get('status')),
      joinedFrom: searchParams.get('joinedFrom') || '',
      joinedTo: searchParams.get('joinedTo') || '',
    };
  }, [searchParams]);

  const apiParams = useMemo<CustomerListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.search ? { search: state.search } : {}),
      ...(state.email ? { email: state.email } : {}),
      ...(state.city ? { city: state.city } : {}),
      ...(state.state ? { state: state.state } : {}),
      ...(state.pincode ? { pincode: state.pincode } : {}),
      ...(state.status !== 'all' ? { status: state.status } : {}),
      ...(state.joinedFrom ? { joinedFrom: state.joinedFrom } : {}),
      ...(state.joinedTo ? { joinedTo: state.joinedTo } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (searchParams) => {
          const next = new URLSearchParams(searchParams);
          if (options?.resetPage) next.set('page', '1');

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) next.set('sortBy', patch.sortBy);
          if (patch.sortOrder !== undefined) next.set('sortOrder', patch.sortOrder);
          if (patch.search !== undefined) apply('search', patch.search);
          if (patch.email !== undefined) apply('email', patch.email);
          if (patch.city !== undefined) apply('city', patch.city);
          if (patch.state !== undefined) apply('state', patch.state);
          if (patch.pincode !== undefined) apply('pincode', patch.pincode);
          if (patch.status !== undefined) apply('status', patch.status, ['all']);
          if (patch.joinedFrom !== undefined) apply('joinedFrom', patch.joinedFrom);
          if (patch.joinedTo !== undefined) apply('joinedTo', patch.joinedTo);

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const next = new URLSearchParams(searchParams);
        next.delete('search');
        next.delete('email');
        next.delete('city');
        next.delete('state');
        next.delete('pincode');
        next.delete('status');
        next.delete('joinedFrom');
        next.delete('joinedTo');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.search !== '' ||
      state.email !== '' ||
      state.city !== '' ||
      state.state !== '' ||
      state.pincode !== '' ||
      state.status !== 'all' ||
      state.joinedFrom !== '' ||
      state.joinedTo !== '' ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort };
}
